// src/v2/leases/LeaseRecordDrawer.jsx
//
// Task #9096 — Full lease record drawer (680px, Overview · Financials · Dates)
// over the Leases list. Route-owned: the parent passes leaseId + tab and
// receives tab/paging/close/full/swap intents. Shell mirrors OfficeRecordDrawer
// (shared SlideSheet, CoverBand, CopyLinkButton, PrevNextPager, RecordReturnLink).
//
// Body content is the full reader's LeaseDetailBody in its optional
// variant="drawer" (same reads, bases, currency toggle, permission gates and
// edit modals; supporting read failures surface as retryable alerts).
// While an edit modal or summary regeneration is in flight every exit —
// close, tab, paging, full page, office swap, return link, router push/pop,
// anchors — asks before leaving.
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { UNSAFE_LocationContext, UNSAFE_NavigationContext } from "react-router-dom";
import { T, LAYERS } from "../../app/tokens.js";
import { SlideSheet } from "../../components/SlideSheet.jsx";
import { RecordDrawerHeader } from "../../components/RecordDrawerHeader.jsx";
import { RecordSectionTabs } from "../../components/RecordSectionTabs.jsx";
import { CoverBand } from "../../components/RecordCoverBand.jsx";
import { RecordReturnLink } from "../../components/RecordReturnLink.jsx";
import { LeaseStatusBadge } from "../../components/LeaseStatusBadge.jsx";
import { showToast } from "../../components/toastService.jsx";
import { useOfficeCover } from "../../features/locations/useOfficeCover.js";
import { useFxRates } from "../../data/useFxRates.js";
import { guardRouterNavigator, registerNavigationGuard, syncNavigationIndex, proceedConfirmedNavigation } from "../../features/projects/recordNavigationGuard.js";
import { LEASE_BUCKET_TO_DB_STATUS } from "../../features/leases/leaseStatusLabels.js";
import { leaseTypeLabel } from "../../features/leases/leaseTypes.js";
import { getLeaseById, updateLease } from "../../data/leasesClient.js";
import { NotFoundError } from "../../data/errors.js";
import { buildLeaseUrl } from "../../services/buildUrl.js";
import { useSensitivityPolicy } from "../../contexts/SensitivityPolicyContext.jsx";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { LeaseDetailBody } from "./LeaseDetailBody.jsx";
import { drawerTabsFromFlags } from "./leaseDrawerFinancials.js";
import { LeaseUnavailable, LeaseLoadError, LeaseDrawerSkeleton, LeaseHeaderStrip, leaseHeaderTitle, displayBucket } from "./LeaseDrawerParts.jsx";

const TAB_LABEL = { overview: "Overview", terms: "Terms", financials: "Financials", dates: "Dates" };
const PANEL_SEL = "[data-testid='lease-record-drawer']";
export const LEASE_DISCARD_PROMPT = "A lease edit or summary regeneration is still in progress. Leave anyway?";
const ghostBtn = {
  display: "inline-flex", alignItems: "center", gap: 5, height: 28, padding: "0 10px", border: `1px solid ${T.border}`,
  borderRadius: 8, background: T.card, color: T.dark, fontSize: T.fsXs, fontWeight: T.fwBold, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
};

/** Policy: the lease record (all tabs, incl. financial ones) follows the full reader's leases.page gate. */
export function leaseDrawerTabs(leaseTabs, canSee) {
  if (typeof canSee !== "function" || !canSee("leases.page")) return [];
  return drawerTabsFromFlags(leaseTabs);
}

export function LeaseRecordDrawer({
  leaseId, tab = "overview", currentRole = "viewer", currentUser = null, orderedIds = [],
  onTabChange, onNavigate, onClose, onOpenFull, onOpenOffice, recordReturn = null, onFollowReturn, onLeaseSaved,
}) {
  void currentUser;
  const open = Boolean(leaseId);
  const canWrite = currentRole === "admin" || currentRole === "editor";
  const isAdmin = currentRole === "admin";
  const sensitivity = useSensitivityPolicy();
  const canSeeLeases = sensitivity.canSee("leases.page");
  const { leaseTabs } = useFeatureFlagContext();
  const tabs = useMemo(() => leaseDrawerTabs(leaseTabs, sensitivity.canSee), [leaseTabs, sensitivity]);
  const activeTab = tabs.includes(tab) ? tab : tabs[0] ?? "overview";

  const [load, setLoad] = useState({ status: "loading", lease: null, error: null, id: null });
  const [reload, setReload] = useState(0);
  const [bodyKey, setBodyKey] = useState(0);
  const [detail, setDetail] = useState(null);
  const [support, setSupport] = useState(null);
  const [currencySelection, setCurrencySelection] = useState({ id: leaseId, value: "USD" });
  const fx = useFxRates();
  const displayCcy = currencySelection.id === leaseId ? currencySelection.value : "USD";
  const onCurrencyChange = (value) => setCurrencySelection({ id: leaseId, value });
  useEffect(() => { setCurrencySelection({ id: leaseId, value: "USD" }); }, [leaseId]);
  const asOfSnapshot = useMemo(() => ({ recordId: leaseId, date: new Date() }), [leaseId]);
  const today = asOfSnapshot.date;
  const [bodyBusy, setBodyBusy] = useState({ editing: false, generating: false });
  const [statusBusy, setStatusBusy] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [width, setWidth] = useState(() => (typeof window === "undefined" ? 680 : Math.min(680, window.innerWidth)));
  const bodyRef = useRef(null);
  const busy = bodyBusy.editing || bodyBusy.generating || statusBusy;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  // ── Header read ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!leaseId || !canSeeLeases) return undefined;
    let live = true;
    setLoad({ status: "loading", lease: null, error: null, id: leaseId });
    setDetail(null); setSupport(null); setSyncError(null);
    setBodyBusy({ editing: false, generating: false });
    Promise.resolve().then(() => getLeaseById(leaseId))
      .then((l) => { if (live) setLoad(l ? { status: "ok", lease: l, error: null, id: leaseId } : { status: "missing", lease: null, error: null, id: leaseId }); })
      .catch((error) => {
        if (!live) return;
        setLoad(error instanceof NotFoundError || error?.status === 404
          ? { status: "missing", lease: null, error: null, id: leaseId }
          : { status: "error", lease: null, error, id: leaseId });
      });
    return () => { live = false; };
  }, [leaseId, reload, canSeeLeases]);

  const lease = load.status === "ok" && load.id === leaseId ? load.lease : null;
  const locationId = lease?.locationId ?? null;
  const pinnedDetail = detail?.id === leaseId ? detail : null;
  const pinnedSupport = support?.leaseId === leaseId ? support : null;

  // ── Navigation guard (router push/replace/go, anchors, back/forward) ──────
  const location = useContext(UNSAFE_LocationContext)?.location;
  const navigator = useContext(UNSAFE_NavigationContext)?.navigator;
  useEffect(() => (open && busy ? guardRouterNavigator(navigator) : undefined), [open, busy, navigator]);
  useEffect(() => { syncNavigationIndex(); }, [location?.key]);
  useEffect(() => {
    if (!open) return undefined;
    return registerNavigationGuard({
      isBlocking: () => busyRef.current,
      onBlocked: (proceed) => { if (window.confirm(LEASE_DISCARD_PROMPT)) proceed(); },
    });
  }, [open]);
  /** Every drawer-initiated exit goes through here. */
  const leave = useCallback((fn) => {
    if (busyRef.current && !window.confirm(LEASE_DISCARD_PROMPT)) return;
    proceedConfirmedNavigation(fn);
  }, []);

  // ── Dialog semantics, focus return, resize ────────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    const returnEl = document.activeElement;
    const t = setTimeout(() => {
      const el = bodyRef.current?.closest(PANEL_SEL);
      if (el) { el.setAttribute("role", "dialog"); el.setAttribute("aria-modal", "true"); el.setAttribute("aria-labelledby", "lease-record-title"); }
      bodyRef.current?.querySelector("[data-testid='lease-record-title']")?.focus();
    }, 0);
    const onResize = () => setWidth(Math.min(680, window.innerWidth));
    onResize();
    window.addEventListener("resize", onResize);
    return () => { clearTimeout(t); window.removeEventListener("resize", onResize); setTimeout(() => { if (returnEl?.isConnected) returnEl.focus?.(); }, 0); };
  }, [open]);

  const changeStatus = async (bucket) => {
    if (!lease) return;
    setSyncError(null);
    setStatusBusy(true);
    try {
      const updated = await updateLease(lease.locationId, leaseId, { status: LEASE_BUCKET_TO_DB_STATUS[bucket], leaseStatus: bucket }, { before: lease });
      if (updated) setLoad((s) => (s.id === leaseId ? { ...s, lease: updated } : s));
      try {
        const confirmed = await getLeaseById(leaseId);
        if (confirmed?.leaseStatus !== bucket) throw new Error(`Lease status confirmation mismatch: expected ${bucket}`);
        setLoad((s) => (s.id === leaseId ? { ...s, lease: confirmed } : s));
        showToast("Lease status updated", { type: "success" });
        onLeaseSaved?.();
      } catch (err) {
        setSyncError("Lease status saved, but confirmation could not be completed. The saved value remains displayed.");
        throw err;
      }
    } finally {
      setStatusBusy(false);
    }
  };

  const onBodySaved = useCallback((info) => {
    if (info?.deleted) { onLeaseSaved?.(); proceedConfirmedNavigation(() => onClose?.()); return; }
    setReload((n) => n + 1);
    onLeaseSaved?.();
  }, [onLeaseSaved, onClose]);

  // LEASE-DRAWER-V2: the linked office's Office Media effective cover through
  // the shared Office pipeline (legacy-photo compatibility, last-good on read
  // failure, media-change events, identity race guard keyed to this lease).
  const coverLoc = pinnedSupport?.location ?? null;
  const coverOfficeId = coverLoc?.id && coverLoc.id === locationId ? coverLoc.id : null;
  const cover = useOfficeCover(coverOfficeId, Boolean(coverOfficeId), leaseId, coverLoc?.imageUrl, reload);
  // Share keeps the collection/filter query the drawer was opened over.
  const copyPath = leaseId ? buildLeaseUrl(leaseId, activeTab, location?.search ?? "") : null;
  const address = pinnedSupport?.location?.address ?? lease?.address ?? null;
  const typeKey = pinnedDetail?.type ?? lease?.leaseType ?? lease?.type ?? null;
  const title = lease ? leaseHeaderTitle(lease, pinnedDetail, pinnedSupport?.location) : load.status === "loading" && canSeeLeases ? "Loading lease…" : "Lease";
  const street = address ? address.split(",")[0].trim() : null;
  const city = pinnedSupport?.location?.market ?? lease?.city;
  const addressLine = [street, city && city !== street ? city : null].filter(Boolean).join(", ");
  const typeLabel = typeKey ? leaseTypeLabel(typeKey) || typeKey : null;

  return (
    <SlideSheet open={open} onClose={() => leave(() => onClose?.())} width={width} layer={LAYERS.stickyChrome + 1}
      panelTestId="lease-record-drawer" scrimTestId="lease-record-drawer-scrim" closeTestId="lease-record-drawer-close" hideCloseButton>
      <style>{`
        ${PANEL_SEL} .lrd-tab { position: relative; border: 0; background: transparent; padding: 10px 2px; margin-right: 18px; font: inherit; font-size: ${T.fsSm}px; font-weight: ${T.fwBold}; color: ${T.mid}; cursor: pointer; }
        ${PANEL_SEL} .lrd-tab[aria-selected="true"] { color: ${T.dark}; }
        ${PANEL_SEL} .lrd-tab::after { content: ""; position: absolute; left: 0; right: 0; bottom: -1px; height: 2px; border-radius: 2px; background: ${T.blue}; transform: scaleX(0); transition: transform .18s ease; }
        ${PANEL_SEL} .lrd-tab[aria-selected="true"]::after { transform: scaleX(1); }
        ${PANEL_SEL} .lrd-tab:focus-visible { outline: 2px solid ${T.blue}; outline-offset: 2px; border-radius: 4px; }
        ${PANEL_SEL} .lrd-panel { animation: lrdIn .22s ease both; }
        @keyframes lrdIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { ${PANEL_SEL} .lrd-panel { animation: none; } ${PANEL_SEL} .lrd-tab::after { transition: none; } }
        [data-testid="lease-record-cover"] { margin: -18px -22px 0; }
        ${cover ? `[data-testid="lease-record-drawer-close"] { z-index: 2; }` : ""}
        .lrd-strip { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); }
        .lrd-strip > :first-child { border-left: 0 !important; }
        @media (max-width: 560px) {
          [data-testid="lease-record-body"] { padding-left: 14px !important; padding-right: 14px !important; }
          [data-testid="lease-record-cover"] { margin: -18px -14px 0; }
          ${PANEL_SEL} [data-testid="key-terms-grid"], ${PANEL_SEL} [data-testid="financials-headline-strip"] { grid-template-columns: repeat(2, minmax(0,1fr)) !important; }
        }
      `}</style>
      <div ref={bodyRef} data-testid="lease-record-body" style={{ padding: "18px 22px 44px", boxSizing: "border-box", minHeight: "100%", background: T.tint || T.lightGray }}>
        <div data-testid="lease-record-cover">
          <CoverBand cover={cover} minHeight={184} marginBottom={0}>
            <RecordDrawerHeader testId="lease-record-header" height={184} stripHeight={68} padding="14px 22px 0"
              eyebrow="Lease" titleId="lease-record-title" titleTestId="lease-record-title" title={title} titleText={title}
              returnLink={recordReturn && <RecordReturnLink inline entry={recordReturn} navigate={(to, state) => leave(() => onFollowReturn?.(to, state))} />}
              meta={lease ? <><span data-testid="lease-record-id" style={{ fontFamily: "inherit", fontWeight: T.fwSemibold }}>{lease.id}</span>
                {typeLabel && <> · <span data-testid="lease-record-type">{typeLabel}</span></>}
                {addressLine && <> · <span data-testid="lease-record-address">{addressLine}</span></>}</> : "\u00a0"}
              metaTestId="lease-record-meta" metaText={[lease?.id, typeLabel, address].filter(Boolean).join(" · ")}
              share={copyPath && canSeeLeases ? { path: copyPath, testId: "lease-record-copy-link" } : null}
              fullPage={lease && onOpenFull ? { onClick: () => leave(() => onOpenFull(lease.id, activeTab === "terms" ? "overview" : activeTab)), testId: "lease-record-full-page" } : null}
              pager={{ ids: orderedIds, currentId: leaseId, onNavigate: id => leave(() => onNavigate?.(id)),
                verticalKeys: true, keyboardActive: open && !busy, disabled: busy || !onNavigate, showUnavailable: true,
                previousLabel: "Previous lease", nextLabel: "Next lease", previousTestId: "lease-record-prev", nextTestId: "lease-record-next", positionTestId: "lease-record-position" }}
              onClose={() => leave(() => onClose?.())} closeTestId="lease-record-drawer-close" closeLabel="Close lease"
               strip={<LeaseHeaderStrip lease={pinnedDetail} support={pinnedSupport} headerLease={lease} displayCcy={displayCcy} rates={fx.rates} today={today} translucent={Boolean(cover)} />} />
          </CoverBand>
        </div>

        {syncError && <div role="alert" data-testid="lease-record-sync-error" style={{ marginTop: 10, padding: "7px 12px", background: T.warmBeige, borderRadius: 8, fontSize: T.fsXs, color: T.dark }}>{syncError}</div>}
        {!canSeeLeases && (
          <div role="alert" data-testid="lease-record-forbidden" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: 22, marginTop: 18, fontSize: T.fsSm, color: T.dark }}>
            Your role doesn't include lease records.
          </div>
        )}
        {canSeeLeases && load.status === "loading" && <div style={{ marginTop: 18 }}><LeaseDrawerSkeleton /></div>}
        {canSeeLeases && load.status === "missing" && <LeaseUnavailable isAdmin={isAdmin} onFollow={(to) => leave(() => onFollowReturn?.(to))} />}
        {canSeeLeases && load.status === "error" && <LeaseLoadError error={load.error} onRetry={() => setReload((n) => n + 1)} />}

        {lease && tabs.length > 0 && (
          <>
            <RecordSectionTabs tabs={tabs.map(key => ({ key, label: TAB_LABEL[key] }))} active={activeTab}
              onChange={key => key !== activeTab && leave(() => onTabChange?.(key))} label="Lease record"
              tabId={key => `lease-tab-${key}`} panelId={() => "lease-record-panel"} tabTestId={key => `lease-record-tab-${key}`} />
            <section role="tabpanel" id="lease-record-panel" aria-labelledby={`lease-tab-${activeTab}`} data-testid={`lease-record-panel-${activeTab}`} className="lrd-panel" style={{ marginTop: 14 }}>
              <LeaseDetailBody
                key={leaseId}
                variant="drawer"
                currencyControl={{ value: displayCcy, onChange: onCurrencyChange, ...fx }}
                asOf={today}
                recordStatus={<LeaseStatusBadge status={displayBucket(lease)} canWrite={canWrite && !bodyBusy.editing && !bodyBusy.generating} onChange={changeStatus} />}
                leaseId={leaseId}
                locationId={locationId}
                currentRole={currentRole}
                refreshKey={bodyKey}
                activeTab={activeTab}
                onTabChange={(k) => tabs.includes(k) && leave(() => onTabChange?.(k))}
                onLeaseLoaded={setDetail}
                onSupportingLoaded={setSupport}
                onBusyChange={setBodyBusy}
                onLeaseSaved={onBodySaved}
                onOpenOffice={onOpenOffice ? (locId) => leave(() => onOpenOffice(locId, lease)) : undefined}
              />
            </section>
            {pinnedSupport?.readErrors?.length > 0 && (
              <button type="button" data-testid="lease-record-reload-all" onClick={() => { setReload((n) => n + 1); setBodyKey((n) => n + 1); }}
                style={{ ...ghostBtn, marginTop: 4 }}>Reload lease</button>
            )}
          </>
        )}
      </div>
    </SlideSheet>
  );
}

export default LeaseRecordDrawer;
